import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, FileText, XCircle } from "lucide-react";
import { BACKEND } from "@/lib/api";
import { fmtEuro, fmtDay } from "@/lib/format";
import { Btn, Field, Spinner } from "@/components/rp";
import { AccessData } from "@/components/commesse";

const err = (e) => e?.response?.data?.detail || "Qualcosa non ha funzionato, riprova";

const Done = ({ ok, text }) => (
  <div className="rp-card p-8 text-center space-y-3" data-testid="public-quote-done">
    {ok ? <CheckCircle2 className="h-12 w-12 mx-auto text-[#198754]" /> : <XCircle className="h-12 w-12 mx-auto text-[#757575]" />}
    <p className="text-xl font-semibold">{ok ? "Preventivo accettato, grazie!" : "Preventivo rifiutato"}</p>
    <p className="text-sm text-[#757575]">{text}</p>
  </div>
);

const AcceptForm = ({ q, call }) => {
  const [f, setF] = useState({ name: q.client_name || "", accept: false, logistics_ok: !!q.logistics_confirmed_at });
  const [note, setNote] = useState("");
  const [rej, setRej] = useState(false);
  return (
    <div className="rp-card p-6 space-y-4" data-testid="public-quote-accept">
      <p className="text-lg font-semibold">Accetta il preventivo</p>
      {!q.logistics_confirmed_at && (
        <label className="flex gap-3 text-sm"><input type="checkbox" checked={f.logistics_ok} onChange={(e) => setF({ ...f, logistics_ok: e.target.checked })} data-testid="public-quote-logistics-ok" /> Confermo che i dati di accesso al cantiere qui sopra sono corretti.</label>
      )}
      <label className="flex gap-3 text-sm"><input type="checkbox" checked={f.accept} onChange={(e) => setF({ ...f, accept: e.target.checked })} data-testid="public-quote-accept-check" /> Dichiaro di aver letto i documenti del preventivo e di accettarlo.</label>
      <Field label="Firma: nome e cognome"><input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="rp-field" data-testid="public-quote-name" /></Field>
      <div className="flex flex-wrap gap-2">
        <Btn onClick={() => call("accept", f)} disabled={!f.accept || !f.logistics_ok || f.name.trim().length < 3} data-testid="public-quote-accept-button"><CheckCircle2 className="h-4 w-4" /> Accetta e firma</Btn>
        <Btn variant="secondary" onClick={() => setRej(!rej)} data-testid="public-quote-reject-toggle">Non mi interessa</Btn>
      </div>
      {rej && (
        <div className="space-y-2">
          <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} className="rp-field" placeholder="Vuoi dirci il motivo? (facoltativo)" data-testid="public-quote-reject-note" />
          <Btn variant="danger" onClick={() => call("reject", { note })} data-testid="public-quote-reject-button">Rifiuta il preventivo</Btn>
        </div>
      )}
    </div>
  );
};

export default function PreventivoPubblico() {
  const { qid } = useParams();
  const [params] = useSearchParams();
  const t = params.get("t") || "";
  const [q, setQ] = useState(null);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);
  const url = `${BACKEND}/api/public/quotes/${qid}`;
  useEffect(() => { axios.get(url, { params: { t } }).then((r) => setQ(r.data)).catch((e) => setError(err(e))); }, [url, t]);
  const call = async (action, body) => {
    try { await axios.post(`${url}/${action}`, body, { params: { t } }); setDone(action); } catch (e) { setError(err(e)); }
  };
  return (
    <div className="min-h-screen bg-[#F7F7F7]">
      <header className="bg-[#070707] px-6 py-5"><img src="/logo-white.png" alt="RP Home" className="h-8" /></header>
      <main className="max-w-2xl mx-auto px-4 py-10 space-y-6">
        {error && <p className="rounded-2xl bg-[#F8D7DA] text-[#842029] p-4 text-sm" data-testid="public-quote-error">{error}</p>}
        {!q && !error && <Spinner />}
        {q && (
          <>
            <div className="rp-card p-6 space-y-2" data-testid="public-quote-card">
              <p className="rp-label">Preventivo {q.code}</p>
              <h1 className="text-3xl font-semibold tracking-tight" data-testid="public-quote-title">{q.title}</h1>
              <p className="text-2xl font-semibold" data-testid="public-quote-amount">{fmtEuro(q.amount)}</p>
              <p className="text-sm text-[#757575]">Per {q.client_name}{q.valid_until && ` · valido fino al ${fmtDay(q.valid_until)}`} · {q.delivery_address}</p>
              {q.client_description && <p className="text-sm whitespace-pre-wrap pt-2">{q.client_description}</p>}
            </div>
            {q.documents.length > 0 && (
              <div className="rp-card p-6 space-y-2" data-testid="public-quote-docs">
                <p className="rp-label mb-2">Documenti</p>
                {q.documents.map((d) => (
                  <a key={d.id} href={`${BACKEND}${q.files[d.id]}`} target="_blank" rel="noreferrer" className="flex items-center gap-3 p-3 rounded-2xl bg-white border border-[#EBEBEB] hover:bg-[#F7F7F7]" data-testid={`public-quote-doc-${d.id}`}>
                    <FileText className="h-5 w-5" /> <span className="text-sm font-medium">{d.filename}</span>
                  </a>
                ))}
              </div>
            )}
            <div className="rp-card p-6"><p className="rp-label mb-3">Dati di accesso al cantiere</p><AccessData x={q} /></div>
            {done ? <Done ok={done === "accept"} text={done === "accept" ? "Abbiamo aperto il tuo cantiere: potrai seguire i lavori nell'Area Riservata (se non sei ancora registrato, ti arriva l'email per scegliere la password)." : "Grazie per averci fatto sapere. Restiamo a disposizione."} />
              : ["inviato", "in_trattativa"].includes(q.status) ? <AcceptForm q={q} call={call} />
              : <p className="rp-card p-6 text-sm" data-testid="public-quote-status">Stato del preventivo: <b>{q.status_label}</b></p>}
          </>
        )}
      </main>
    </div>
  );
}
